import { CUSTOMER_CREDITS_SPACE_PARAMETER_SUPPORT } from '@src/components/AppLayout/Panels/Dealer/DealerSpaceParameters';
import { useSpaceParameterControlledFilter } from '@src/components/AppLayout/SpaceParameters';
import { useCounterpartiesFilter } from '@src/hooks';
import { useCustomers } from '@src/hooks/useCustomer';
import { OrgConfigurationKey, useCustomerCredit, useCustomerMarketAccounts, useOrgConfiguration } from '@src/providers';
import { EntityAdmin, type CustomerCredit } from '@talos/kyoko';
import { useMemo } from 'react';
import { CustomerCreditsSummaryLine } from './CustomerCreditsSummaryLine';
import {
  CUSTOMER_CREDITS_COLUMNS,
  CUSTOMER_CREDITS_WS_REQUEST,
  DEFAULT_CUSTOMER_CREDITS_CURRENCIES,
  getCustomerCreditsDrawerOptions,
  getCustomerCreditsEntityAsDrawerEntity,
  type CustomerCreditDrawerEntity,
} from './utils';

export const CustomerCredits = () => {
  const customers = useCustomers();
  const { customerMarketAccountsList } = useCustomerMarketAccounts();
  const { customerCreditByMarketAccount, customerCreditCount } = useCustomerCredit();
  const { getConfig } = useOrgConfiguration();

  const customerCreditCurrencies = useMemo(() => {
    const additionalCreditCurrencies = getConfig(OrgConfigurationKey.AdditionalCustomerCreditCurrencies, '')
      .split(',')
      .map(currency => currency.trim())
      .filter(currency => !!currency); // guard against trailing commas leading to empty strings

    const allCurrencies = [...DEFAULT_CUSTOMER_CREDITS_CURRENCIES];

    for (const currency of additionalCreditCurrencies) {
      allCurrencies.push({ Symbol: currency, Description: '' });
    }

    return allCurrencies;
  }, [getConfig]);

  const drawerOptions = useMemo(() => {
    if (customers != null && customerMarketAccountsList != null && customerCreditByMarketAccount != null) {
      return getCustomerCreditsDrawerOptions(
        customers,
        customerMarketAccountsList,
        customerCreditByMarketAccount,
        customerCreditCurrencies
      );
    }
  }, [customerMarketAccountsList, customers, customerCreditByMarketAccount, customerCreditCurrencies]);

  const counterpartiesFilter = useCounterpartiesFilter();
  const availableFilterableProperties = useMemo(() => [counterpartiesFilter], [counterpartiesFilter]);
  const { externallyControlledFilter, filterableProperties } = useSpaceParameterControlledFilter(
    CUSTOMER_CREDITS_SPACE_PARAMETER_SUPPORT,
    availableFilterableProperties
  );
  const maxCustomerCreditLimitConfig = getConfig(OrgConfigurationKey.CustomerMaxCustomerCreditLimitCount, '');
  const maxCustomerCreditLimitCount = getCustomerCreditLimitCount(maxCustomerCreditLimitConfig);

  return (
    <EntityAdmin<CustomerCredit, CustomerCreditDrawerEntity>
      wsRequest={CUSTOMER_CREDITS_WS_REQUEST}
      path="/customer-credit"
      title="Customer Credit"
      subtitle="Manage customer credit limits"
      entityName="Credit Limit"
      blotterTableFooter={
        customerCreditCount != null && maxCustomerCreditLimitCount != null ? (
          <CustomerCreditsSummaryLine currentCount={customerCreditCount} maxCount={maxCustomerCreditLimitCount} />
        ) : undefined
      }
      entityIDField="MarketAccount"
      columns={CUSTOMER_CREDITS_COLUMNS}
      allowAddEntity={true}
      allowEditEntity={true}
      drawerOptions={drawerOptions}
      getEntityAsDrawerEntity={getCustomerCreditsEntityAsDrawerEntity}
      filterableProperties={filterableProperties}
      externallyControlledFilter={externallyControlledFilter}
      filterableServerFields={['Counterparty']}
      persistKey="dealer/credit"
      confirmTextActions={['single-edit', 'single-delete']}
      /* In the context of CustomerCredit, all POST, PATCH, and DELETE requests are made to the same endpoint.
       * The default behavior would append the entityID to the path, but in this case, we don't want that. */
      getPostPath={() => '/customer-credit/limits'}
      getPatchPath={() => '/customer-credit/limits'}
      usePutForPatchPost={true}
    />
  );
};

function getCustomerCreditLimitCount(value: string | undefined): number | undefined {
  if (value == null || value.trim() === '') {
    return undefined;
  }

  const count = Number(value);
  return Number.isInteger(count) && count >= 0 ? count : undefined;
}
